import type { ReactNode } from 'react'
import { ApiError } from '@/api/errors'
import { Button } from './Button'
import { cn } from '@/lib/cn'

/**
 * Estados de tela: carregando, vazio, erro.
 *
 * Requisitos que isto atende:
 *  - nunca tela branca, nunca "Carregando…" eterno (B-23: 6 catches vazios no
 *    sistema antigo deixavam a tela presa para sempre);
 *  - erro sempre legível em português, com ação de tentar de novo;
 *  - offline e 403 tratados — no sistema antigo não eram tratados em lugar nenhum (B-24);
 *  - estado vazio desenhado, não um texto solto (importa muito no Financeiro,
 *    que está praticamente sem dados).
 */

/* ------------------------------------------------------------- carregando */

/** Bloco cinza que imita o conteúdo que vai chegar. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-ink-100', className)} />
}

/** Esqueleto de tabela — evita o salto de layout quando os dados chegam. */
export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="px-3 py-2" aria-busy aria-label="Carregando dados">
      <div className="flex gap-3 border-b border-ink-200 pb-2">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3 border-b border-ink-100 py-2.5">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={cn('h-3.5 flex-1', c === 0 && 'max-w-28')} />
          ))}
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ vazio */

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-14 text-center', className)}>
      {icon && (
        <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-ink-100 text-ink-400">
          {icon}
        </div>
      )}
      <h3 className="text-sm font-semibold text-ink-700">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-xs text-ink-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

/* ------------------------------------------------------------------- erro */

const TITULO_POR_TIPO: Record<string, string> = {
  offline: 'Sem conexão',
  rede: 'Não foi possível conectar',
  timeout: 'A operação demorou demais',
  siscomex_desconectado: 'SISCOMEX desconectado',
  siscomex_bloqueado: 'Aguarde para tentar de novo',
  siscomex_erro: 'O SISCOMEX recusou a operação',
  operacao_falhou: 'Não foi possível concluir',
  sem_permissao: 'Sem permissão',
  nao_encontrado: 'Não encontrado',
  validacao: 'Dados inválidos',
  servidor: 'Erro no servidor',
  resposta_invalida: 'Resposta inesperada do servidor',
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown
  onRetry?: () => void
  className?: string
}) {
  const api = error instanceof ApiError ? error : null
  const titulo = api ? (TITULO_POR_TIPO[api.kind] ?? 'Algo deu errado') : 'Algo deu errado'
  const msg =
    api?.message ??
    (error instanceof Error ? error.message : 'Erro inesperado. Tente novamente.')
  const podeTentar = onRetry && (api?.recuperavel ?? true)

  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-danger-50 text-danger-600">
        <svg viewBox="0 0 20 20" fill="none" className="size-5" aria-hidden>
          <path
            d="M10 7v4m0 3h.01M10 2.5 2.5 16h15L10 2.5Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <h3 className="text-sm font-semibold text-ink-800">{titulo}</h3>
      <p className="mt-1 max-w-md text-xs text-ink-600">{msg}</p>

      {/* Código real do Portal Único: é o que o despachante usa para entender a recusa. */}
      {api?.siscomexCode && (
        <p className="mt-1.5 font-mono text-2xs text-ink-500">Código SISCOMEX: {api.siscomexCode}</p>
      )}

      <div className="mt-4 flex items-center gap-2">
        {podeTentar && (
          <Button variant="primary" size="sm" onClick={onRetry}>
            Tentar de novo
          </Button>
        )}
        {api?.kind === 'siscomex_desconectado' && (
          <Button variant="secondary" size="sm" onClick={() => (window.location.href = '/settings')}>
            Reconectar ao SISCOMEX
          </Button>
        )}
      </div>

      {/* Detalhe técnico fica escondido: útil no suporte, sem poluir a tela. */}
      {api?.detail != null && (
        <details className="mt-4 max-w-lg text-left">
          <summary className="cursor-pointer text-2xs text-ink-400 hover:text-ink-600">
            Detalhes técnicos
          </summary>
          <pre className="mt-1.5 max-h-40 overflow-auto rounded border border-ink-200 bg-ink-50 p-2 font-mono text-2xs whitespace-pre-wrap text-ink-600">
            {typeof api.detail === 'string' ? api.detail : JSON.stringify(api.detail, null, 2)}
          </pre>
        </details>
      )}
    </div>
  )
}

/* ------------------------------------------------------- faixa de aviso */

export function Alert({
  tone = 'info',
  title,
  children,
  action,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success'
  title?: string
  children?: ReactNode
  action?: ReactNode
}) {
  const cores = {
    info: 'bg-brand-50 text-brand-800 ring-brand-200',
    warning: 'bg-warning-50 text-warning-700 ring-warning-200',
    danger: 'bg-danger-50 text-danger-700 ring-danger-200',
    success: 'bg-success-50 text-success-700 ring-success-200',
  }[tone]

  return (
    <div className={cn('flex items-start gap-3 rounded-md px-3 py-2.5 ring-1 ring-inset', cores)}>
      <div className="min-w-0 flex-1">
        {title && <p className="text-sm font-semibold">{title}</p>}
        {children && <div className={cn('text-xs', title && 'mt-0.5')}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}
